import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, combineLatest } from 'rxjs';
import { concatMap, map, switchMap, tap } from 'rxjs/operators';
import { BazaNcIntegrationListingsDto } from '@scaliolabs/baza-nc-integration-shared';
import {
    BazaNcBootstrapDto,
    BazaNcOfferingRegulationType,
    BazaNcPurchaseFlowTransactionType,
    KYCStatus,
    PurchaseFlowDto,
    PurchaseFlowSessionDto,
    StatsDto,
} from '@scaliolabs/baza-nc-shared';
import { BazaLoadingService } from '@scaliolabs/baza-core-ng';
import { Verification } from '@scaliolabs/baza-web-verification-flow';
import { Calculator } from '@scaliolabs/baza-web-ui-components';
import { BazaWebUtilSharedService, LEGACY_NC_MODE_QUERY_PARAM } from '@scaliolabs/baza-web-utils';

import { CancelPurchase, GetPurchaseStats, PatchStartPurchase, PurchaseState, StartPurchase } from '../data-access';

@Component({
    selector: 'app-purchase-details-recurring',
    templateUrl: './details-recurring.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseDetailsRecurringComponent implements OnInit {
    @Input()
    initData: BazaNcBootstrapDto;

    @Input()
    verification: Verification;

    cart$ = this.store.select(PurchaseState.cart);
    stats$ = this.store.select(PurchaseState.stats);
    numberOfShares$ = this.store.select(PurchaseState.numberOfShares);
    purchaseStart$ = this.store.select(PurchaseState.purchaseStart);

    frequency: string;
    numberOfShares: number;
    i18nBasePath = 'dwpf.details';

    item: BazaNcIntegrationListingsDto;
    stats: StatsDto;

    OFFERING_REGULATION_TYPES = BazaNcOfferingRegulationType;
    pfConfig = this.store.selectSnapshot(PurchaseState.pfConfig);

    dataStream$: Observable<{
        number: number;
        stats: StatsDto;
    }>;

    shareMapping: { [k: string]: string } = {
        '=1': '# share',
        other: '# shares',
    };
    KycStatus = KYCStatus;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly store: Store,
        public readonly loader: BazaLoadingService,
        public readonly wts: BazaWebUtilSharedService,
    ) {
        this.item = this.store.selectSnapshot(PurchaseState.cart);

        // cancel purchase + load stats + check configurations (in order)
        this.dataStream$ = this.store.dispatch(new CancelPurchase({ offeringId: this.item?.offeringId })).pipe(
            concatMap(() => this.store.dispatch(new GetPurchaseStats(this.item?.offeringId, this.getMinPurchaseAmount(this.item), true))),
            switchMap(() => {
                return this.initializeDataStream();
            }),
        );
    }

    ngOnInit(): void {
        if (this.wts.isForeignInvestor(this.initData) && !this.route?.snapshot?.queryParams?.foreign) {
            this.router.navigate(['.'], {
                relativeTo: this.route,
                queryParams: {
                    foreign: true,
                },
                queryParamsHandling: 'merge',
            });
        }
    }

    initializeDataStream() {
        return combineLatest([this.numberOfShares$, this.stats$]).pipe(
            map(([number, stats]) => ({
                number,
                stats,
            })),
            tap((pair) => {
                this.stats = pair.stats;
            }),
        );
    }

    public getMinPurchaseAmount(entity: BazaNcIntegrationListingsDto) {
        return entity?.pricePerShareCents * entity?.numSharesMin;
    }

    public onEditPersonalInfo(): void {
        this.store.dispatch(new PatchStartPurchase({ numberOfShares: this.numberOfShares } as PurchaseFlowDto)).subscribe(() => {
            this.router.navigate([this.pfConfig.vfLink, 'info'], {
                queryParams: {
                    back: this.router.url,
                    mode: !this.pfConfig?.dwollaSupport && this.route.snapshot?.queryParams?.mode ? LEGACY_NC_MODE_QUERY_PARAM : null,
                },
            });
        });
    }

    public onCalcChange(form: Calculator) {
        this.frequency = form.frequency;
        this.numberOfShares = form.amount;
    }

    public onNextButtonClick() {
        const purchase: PurchaseFlowSessionDto = {
            amount: this.numberOfShares * this.item?.pricePerShareCents,
            numberOfShares: this.numberOfShares,
            offeringId: this.item?.offeringId,
            transactionType: BazaNcPurchaseFlowTransactionType.ACH,
        };

        this.store.dispatch(new StartPurchase(purchase, true)).subscribe(() => {
            this.router.navigate([this.pfConfig?.pfLink, 'agreement'], { queryParamsHandling: 'preserve' });
        });
    }

    public appAndItemSupportsRegCF(item: BazaNcIntegrationListingsDto) {
        return this.wts.appSupportsRegCF(this.initData) && item?.schema?.regulationType === BazaNcOfferingRegulationType.RegCF;
    }

    public get showSharePickerError() {
        return (
            this.stats?.totalOfferingSharesRemaining === 0 ||
            this.stats?.numMinPurchaseAbleShares > this.stats?.totalOfferingSharesRemaining ||
            this.stats?.canPurchase === 0
        );
    }
}
